/**
 * @module app.cd
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/api/api';
import { MemberService } from 'app/cd/services/member-service';
import { Toaster } from 'app/shared/lib/toaster';
import { IProviderSearchOptions } from 'app/cd/elements/providers/provider-search-filters';
import { deepClone } from 'app/shared/lib/utilities';
import { getReference } from 'app/shared/models/essette-types';
import { ProviderLocationSelect } from 'app/cd/elements/providers/provider-location-select';
import * as CD from 'app/cd/models/essette-api';

@autoinject
export class SaveMemberProvider extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.objectNull({ changeHandler: 'reload' })
    protected memberProvider: CD.MemberProvider | null = null;

    protected editedMemberProvider: CD.MemberProvider | null = null;

    protected options: IProviderSearchOptions | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    public memberId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected memberPlans: ReadonlyArray<CD.MemberPlan> = [];

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected relationships: ReadonlyArray<CD.MemberProviderRelationship> = [];


    ////****************** Element References ***************************************////
    protected providerSelect: ProviderLocationSelect | null = null;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reload();
    }


    ////****************** Events ***************************************************////
    protected async reload(): Promise<void> {
        if(this.memberProvider) {
            this.editedMemberProvider = deepClone(this.memberProvider);
        } else {
            this.editedMemberProvider = null;
        }
    }


    ////****************** Functions ************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public reset(): void {
        if(this.memberProvider) {
            this.editedMemberProvider = deepClone(this.memberProvider);
        }

        if(this.providerSelect) this.providerSelect.clear();
    }

    public async submit(): Promise<boolean> {
        return this.saveMemberProvider();
    }

    protected buildMemberProvider(): CD.MemberProvider {
        const memberProvider: CD.MemberProvider = {
            id: this.editedMemberProvider?.id,
            member: {
                id: this.memberId,
                plan: getReference(this.editedMemberProvider?.member?.plan)
            },
            provider: {
                id: this.editedMemberProvider?.provider?.location?.provider?.id,
                location: getReference(this.editedMemberProvider?.provider?.location)
            },
            relationship: getReference(this.editedMemberProvider?.relationship),
            dateRange: {
                start: this.editedMemberProvider?.dateRange?.start,
                end: this.editedMemberProvider?.dateRange?.end
            }
        };

        return memberProvider;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async saveMemberProvider(): Promise<boolean> {
        if(!this.editedMemberProvider) return false;

        try {
            this.state.beginSave();

            const response = await this.memberService.saveMemberProvider(this.buildMemberProvider());
            this.toaster.standard.saved('cd:memberProviderRelationship.$', response.isSuccessful);
            return response.isSuccessful;
        }
        catch(error) {
            this.toaster.standard.saved('cd:memberProviderRelationship.$', false);
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }
}
